import { Badge, type BadgeTone } from '@/components/ui';
import { formatDateTime } from '@/lib/format';
import {
  STATUS_IS_LAST_KNOWN_NOTE,
  isTerminalStatus,
  signaturesNotBinding,
  type DocuSignBindingSignals,
  type DocuSignLastKnownStatus,
} from '@/lib/docusign';

/*
 * The one rendering of an envelope's status, so a quote panel, a list row and a
 * detail header can never describe the same envelope differently.
 *
 * Modelled on `XeroStatusBadge` + the `PRESENTATION` map in `@/lib/xero`, with
 * one structural difference that is the whole point of this file:
 *
 * ⚠️ **`status` is NOT an enum.** It is DocuSign's own word, matched exactly,
 * stored as a string of up to 24 characters. The backend says so explicitly and
 * tells clients not to build a `switch` with no default: `created`, `sent`,
 * `delivered`, `signed`, `completed`, `declined`, `voided`, *and whatever
 * DocuSign adds next*. So {@link PRESENTATION} is a **partial lookup over
 * `string`**, deliberately not a `Record<Union, …>` — there is no union to key
 * it by, and inventing one here would turn a status this build has not heard of
 * into a compile error on somebody else's deploy day.
 *
 * An unrecognised word therefore renders as **itself**, neutrally. That is this
 * repo's standing rule in `xero.ts` and `line-project.ts` alike: unrecognised
 * means "newer than this map", never "invalid", and a model that returns
 * nothing for what it does not recognise reports nothing, so the defect stays
 * live.
 */

/**
 * What a status becomes on screen.
 *
 * `terminal` is deliberately **absent** — it is not a property of the
 * presentation, it is read from `isTerminalStatus()` in `@/lib/docusign`, which
 * mirrors the contract's own frozen list. A second hand-maintained copy of
 * "which of these are final" is exactly the kind that agrees until someone
 * edits one of them.
 */
interface EnvelopeStatusPresentation {
  label: string;
  tone: BadgeTone;
  /** Rides along as a `title`. Never the only place a fact lives — see below. */
  detail: string;
  /**
   * This presentation asserts that a signature was obtained.
   *
   * A flag rather than `status === 'completed'` at the one call site, so the
   * day DocuSign adds a second word meaning "they signed", qualifying it is a
   * flag on that entry rather than a condition somebody has to remember to
   * widen. See {@link signaturesNotBinding} for what it qualifies.
   */
  claimsSignature?: boolean;
}

/**
 * DocuSign's words, as a person reads them.
 *
 * Every sentence here is written in the **past tense, as at the last check**,
 * and that is correctness rather than tone. There is **no webhook** — DocuSign
 * Connect is not wired up, so nothing pushes and a stored status only moves
 * when something calls `POST /docusign/envelopes/{id}/refresh`. Copy in the
 * present tense ("waiting for the signer", "the signer is reading it") claims
 * live tracking this app does not have, and would read as a fact about right
 * now when it is a fact about whenever anything last asked.
 *
 * ⚠️ **"Something", not "somebody" — corrected 2026-08-30, and the past tense
 * SURVIVES the correction.** A backend worker re-checks outstanding envelopes on
 * a timer (`BACKGROUND_CHECK_NOTE` in `@/lib/docusign`), so a status can move
 * with nobody here. That makes the stored word fresher on average and no less a
 * snapshot: the pass is periodic, backs off after a failure, and does not run
 * where DocuSign is unconfigured. **A shorter staleness window is not liveness**
 * — do not let this become a reason to move any of these into the present tense.
 *
 * The one exception is the three terminal words, and it is a real exception
 * rather than a slip: a terminal envelope is never re-fetched, because
 * `completed`, `declined` and `voided` are final. Their sentences are stated
 * flatly, because for them the stored copy cannot be stale.
 */
const PRESENTATION: Readonly<Record<string, EnvelopeStatusPresentation>> = {
  /*
   * ⚠️ **`created` is the failure that looks like success**, and it is the
   * reason this entry is toned `warning` rather than `neutral`.
   *
   * DocuSign does not error on a template role that matches no recipient. It
   * creates the envelope, reports success, and leaves it sitting here **for
   * ever with nobody asked to sign** — so an envelope resting at `created` is
   * far more likely to be stuck than in transit. Toning it as "nothing has
   * happened yet" would file the worst outcome the send has as the calmest
   * thing on the screen.
   */
  created: {
    label: 'Not sent',
    tone: 'warning',
    detail:
      'Exists in DocuSign but nobody has been asked to sign. Usually stuck, not in transit.',
  },
  sent: {
    label: 'Sent',
    tone: 'info',
    detail: 'Gone out, not opened as of the last check.',
  },
  /*
   * ⚠️ **`delivered` does NOT mean an email arrived.** It is DocuSign's word
   * for the recipient having opened the envelope, and it is reported for an
   * embedded signer who was never emailed at all. "Delivered" as a label would
   * be read as a mail-server receipt by everyone who has ever seen one.
   */
  delivered: {
    label: 'Opened',
    tone: 'info',
    detail: 'Opened, not signed, as of the last check. Opened — not an email receipt.',
  },
  /*
   * ⚠️ **DocuSign's `signed` is not our `signed`.** The envelope DTO's own
   * `signed` boolean is `status === "completed"`, specifically — so an envelope
   * sitting on this word has a signature on it and `signed: false` on the
   * record. Two things one word apart, which is why the label says so out loud
   * instead of reading as done.
   *
   * Not `claimsSignature`, and that is not an oversight: the qualification
   * below turns a claim of a FINISHED signature into a warning, and this label
   * already refuses to make that claim.
   */
  signed: {
    label: 'Partly signed',
    tone: 'info',
    detail: 'Somebody signed, but the envelope is not complete. Does not count as signed here.',
  },
  completed: {
    label: 'Signed',
    tone: 'success',
    detail: 'Everyone asked to sign has signed. This is final.',
    claimsSignature: true,
  },
  declined: {
    label: 'Declined',
    tone: 'danger',
    detail: 'The signer refused. Final — a new envelope is the only way on.',
  },
  /*
   * Voided is toned `neutral`, not `danger`. It is withdrawn deliberately, by
   * somebody here, for a reason they typed — a decision rather than a failure,
   * and colouring our own action as an error is how a screen teaches people to
   * disregard the colour that does mean something.
   */
  voided: {
    label: 'Voided',
    tone: 'neutral',
    detail: 'Withdrawn, and the signer was told. Final — a new envelope is the only way on.',
  },
};

export interface EnvelopeStatusBadgeProps {
  /**
   * DocuSign's own word, exactly as stored.
   *
   * `DocuSignLastKnownStatus` is a `string` alias, not a union — see the note on
   * {@link PRESENTATION}. It is used in preference to a bare `string` because
   * the alias exists to carry that warning to the point of use, and its name
   * carries the other half: it is the status as of the last `/refresh`.
   */
  status: DocuSignLastKnownStatus;
  /**
   * The DocuSign connection, for the not-legally-binding qualification.
   *
   * ⚠️ **Omitting it warns rather than reassures**, and that is deliberate.
   * {@link signaturesNotBinding} is a negative check — "warn unless provably
   * production" — so `undefined` here resolves to *not binding* and a caller
   * who forgets to thread the connection through gets the caveat, never a
   * false all-clear. Failing open is the default that rule exists to invert.
   *
   * Typed as {@link DocuSignBindingSignals} rather than `DocuSignConnection`,
   * so a partial or hand-built object — a 502 with nothing in it — passes
   * straight in, exactly as that module intends.
   */
  connection?: DocuSignBindingSignals | null;
  /**
   * The server's computed `terminal` flag off the envelope.
   *
   * Optional, and only consulted for a status {@link PRESENTATION} does not
   * know. The backend computes `terminal` and `signed` "precisely so the common
   * questions do not need a switch", so for an unrecognised word this is the
   * only trustworthy answer available — this build cannot classify a word it
   * has never seen, but the server already did.
   */
  terminal?: boolean;
  /**
   * `statusCheckedAt` — when anyone last asked DocuSign.
   *
   * Kept separate from `updatedAt` by the contract for a reason worth
   * preserving: "we asked two minutes ago and it is still out" and "nobody has
   * asked since Tuesday" are different facts. It is added to the tooltip only
   * where it means something — a terminal envelope is never re-checked, so
   * dating its finality would imply a doubt that does not exist.
   */
  statusCheckedAt?: string | null;
  className?: string;
}

/**
 * An envelope's status as a word, a tone and a shape.
 *
 * ⚠️ **`completed` must never read as an unqualified success while DocuSign is
 * in sandbox.** The credentials on the server are the DocuSign sandbox,
 * deliberately, and a sandbox envelope carries no legal force — so the failure
 * mode looks exactly like success: somebody sends a customer a signing link,
 * watches it come back "signed", and has nothing.
 *
 * The qualification changes the **word**, not only the tone. House rule is that
 * colour is never the only signal, and there is a second reason here: the
 * detail rides on a `title`, which is unreachable on touch and invisible to a
 * screen reader that is not hovering. A caveat that lived only in the tooltip
 * or only in the colour would be absent from exactly the reading that matters.
 *
 * {@link signaturesNotBinding} is the ONE copy of that rule. It is consumed,
 * never re-derived — there is no `=== 'demo'` test here and there must not be
 * one, because that comparison silently stops warning the day a third
 * environment appears. When real credentials land, `environment` flips on its
 * own and this badge stops qualifying with no edit here or at any call site.
 */
export function EnvelopeStatusBadge({
  status,
  connection,
  terminal,
  statusCheckedAt,
  className,
}: EnvelopeStatusBadgeProps) {
  const known = PRESENTATION[status];

  /*
   * The unrecognised branch. Render the raw word, neutrally.
   *
   * Verbatim rather than prettified: it is the string the API filters on and
   * the string a support ticket will quote, so altering it costs the one thing
   * it is good for. `neutral` because a word this build cannot interpret must
   * not be given a tone that interprets it — guessing `success` from a
   * hopeful-looking word is how a badge asserts something nobody checked.
   *
   * A blank status is the one thing that cannot be shown raw, since a badge
   * with no text in it reads as a rendering fault rather than as a state.
   */
  const raw = status.trim();
  const label = known ? known.label : raw || 'Unknown status';
  const tone: BadgeTone = known ? known.tone : 'neutral';

  /*
   * Terminality: the contract's list for a word we know, the server's computed
   * flag for one we do not. Never `?? false` on the prop — an absent flag on an
   * unknown status means nobody has said, and treating that as "still running"
   * is a guess in the direction that adds a staleness caveat, which is the safe
   * one of the two.
   */
  const isTerminal = known ? isTerminalStatus(status) : Boolean(terminal);

  const notBinding = signaturesNotBinding(connection);
  const qualify = Boolean(known?.claimsSignature) && notBinding;

  const detail = known
    ? known.detail
    : `DocuSign reported "${raw || '(blank)'}" — newer than this screen, not wrong.`;

  /*
   * The tooltip, assembled in the order a reader needs it: what the state means,
   * then whether the signature counts for anything, then how old the answer is.
   */
  const notes = [detail];

  if (qualify) {
    notes.push(
      'NOT legally binding — the DocuSign connection is a sandbox.',
    );
  }

  /*
   * Only for a non-terminal envelope. `completed`, `declined` and `voided` are
   * final and are never re-fetched, so "we last checked on Tuesday" beside one
   * of them would invite a refresh that would do nothing and imply the answer
   * might since have moved. It cannot.
   */
  if (!isTerminal) {
    notes.push(
      statusCheckedAt
        ? `${STATUS_IS_LAST_KNOWN_NOTE} DocuSign was last asked on ${formatDateTime(statusCheckedAt)}.`
        : STATUS_IS_LAST_KNOWN_NOTE,
    );
  }

  return (
    <span className="inline-flex" title={notes.join(' ')}>
      <Badge tone={qualify ? 'warning' : tone} dot className={className}>
        {qualify ? `${label} — not binding` : label}
      </Badge>
    </span>
  );
}
